import { differenceInCalendarDays, format, parseISO, subDays } from "date-fns"
import { decodeHTML } from "entities"

import {
  PropertyAttachmentType,
  PropertyDataV2,
  PropertyTypeV2,
} from "commonModule/services/common"
import { FALLBACK_LOGO_IMAGE } from "constant"

import { capitalize } from "./misc"

// Formats a property type description with proper capitalization and plurality
export function getPropertyType(propertyTypeDesc: string, isPlural = false) {
  if (!propertyTypeDesc || propertyTypeDesc === "Undefined") {
    return isPlural ? `Properties ` : `Property `
  }

  if (propertyTypeDesc === "Home") {
    return isPlural ? `Private Homes` : `Private Home`
  }

  const capitalizedType = capitalize(propertyTypeDesc)
  return isPlural ? `${capitalizedType}s` : capitalizedType
}

export function getPropertyTypeDesc(
  propertyTypeDesc: string,
  city: string,
  state: string,
  country: string | null,
  postalName?: string | null,
  isMultiUnitProperty?: boolean,
  bedsCount?: string,
  count?: number | null,
) {
  const propertyTypeStr = getPropertyType(propertyTypeDesc)

  const COUNTRY_LIST = ["us", "usa", "ca", "gn", "united kingdom"]

  const showCityState =
    COUNTRY_LIST.includes((country || "").trim().toLowerCase()) ||
    !country ||
    country.trim() === ""
  let location = ""

  // if country is US / CA / GB / United Kingdom / missing country → City, State
  if (showCityState) {
    location = [city, postalName, state]?.filter((x) => !!x)?.join(", ") || ""
  }
  // for Other countries  → City, Country
  else {
    location = [city, country]?.filter((x) => !!x)?.join(", ") || ""
  }

  return isMultiUnitProperty
    ? `${count ? count : "Multiple"} units in ${location}`
    : bedsCount && bedsCount !== ""
    ? `${bedsCount} in ${location}`
    : `${propertyTypeStr} in ${location}`
}

export function getLocationDisplay(property: PropertyTypeV2) {
  const city = property?.city || ""
  const postalName = property?.postal_name || ""
  const state = property?.state || ""
  const country = property?.country || ""
  const isUSorCA =
    country === "US" ||
    country === "USA" ||
    country === "CA" ||
    !country ||
    country.trim() === ""

  // if country is US / CA / missing country
  if (isUSorCA) {
    return `${city ? `${city}, ` : ""}${
      postalName ? `${postalName}, ` : ""
    }${state}`
  }
  return `${city ? `${city}, ` : ""}${country}`
}

const byImgOrder = (e: PropertyAttachmentType) => e.image_order === 0
const byCoverImg = (e: PropertyAttachmentType) => e.is_cover_img
const byImgOrderOne = (e: PropertyAttachmentType) => e.image_order === 1

export function getCoverImg(
  attachments: PropertyAttachmentType[],
  noSort = true,
) {
  if (noSort) {
    return (
      attachments?.[0]?.image_full_url ||
      attachments?.[0]?.file_url ||
      FALLBACK_LOGO_IMAGE
    )
  }

  if (attachments && attachments[0]) {
    return (
      attachments.find(byImgOrder)?.image_full_url ||
      attachments.find(byCoverImg)?.image_full_url ||
      attachments.find(byImgOrderOne)?.image_full_url ||
      attachments[0]?.image_full_url
    )
  }
  return FALLBACK_LOGO_IMAGE
}

export function findMostCommonCity(data: PropertyDataV2[]) {
  const cityCounts: Record<string, number> = {}
  data.forEach((item) => {
    cityCounts[item.city] = (cityCounts[item.city] ?? 0) + 1
  })
  const mostCommonCity = Object.keys(cityCounts).reduce((a, b) =>
    cityCounts[a] > cityCounts[b] ? a : b,
  )
  const result = data.find((item) => item.city === mostCommonCity)
  return result
}

export function decodeHTMLEntities(text: string): string {
  if (!text || typeof text !== "string") {
    return "" // Return an empty string if the input is invalid
  }
  return decodeHTML(text)
}

export const calculateFormattedBeforeDate = (
  checkInDateStr: string | null,
  validToDays: number,
): { formattedBeforeDate: string; isPastDate: boolean } => {
  if (!checkInDateStr || validToDays <= 0) {
    return { formattedBeforeDate: "", isPastDate: true }
  }

  const checkInDate = parseISO(checkInDateStr)
  const beforeDate = subDays(checkInDate, validToDays)
  const today = new Date()

  const isPastDate = differenceInCalendarDays(beforeDate, today) < 0

  if (isPastDate) {
    const daysLeft = differenceInCalendarDays(checkInDate, beforeDate)
    return {
      formattedBeforeDate: `${daysLeft} days`,
      isPastDate: true,
    }
  }
  return {
    formattedBeforeDate: format(beforeDate, "MMM d, yyyy"),
    isPastDate,
  }
}
